import { combineEpics, ofType } from 'redux-observable';
import NewHistoryDuck from 'ducks/new-history-duck';
import NewBookingDuck from 'ducks/new-booking-duck';
import { mergeMap, catchError } from 'rxjs/operators';
import { of, EMPTY, forkJoin } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import fetchObservableUtil, {
  catchAndReturnError,
} from 'common/utils/fetch-observable';
import endpoints, {
  getCancelDeliveryEpt,
  getCancelReasonsEpt,
  getCancelReasonsWithDeliveryID,
  getPaxCancelDeliveryEpt,
} from 'actions/history-endpoints';
import { buildHeadersV2, buildAjaxCredentials } from 'helper/ajax-helper';
import {
  enableCancellationRulesSelector,
  enterpriseSelector,
  isPaxSelector,
  userCompanyIDSelector,
} from 'selectors/app-selector';
import { senderContactSelector } from 'selectors/new-booking-selectors';
import { historyBookingFilterSelector } from 'selectors/new-history-selectors';
import moment from 'moment';
import _get from 'lodash/get';
import _every from 'lodash/every';
import _compact from 'lodash/compact';
import _orderBy from 'lodash/orderBy';
import {
  GE_ORDER_BOOK_AGAIN_STATES,
  GE_ORDER_ONGOING_STATES,
} from 'constants/order-constants';
import {
  GFB_ORDER_SUMMARIZE_FETCH_DAYS,
  PAX_ORDER_SUMMARIZE_FETCH_DAYS,
} from 'constants/history-constants';
import { FETCH_CANCEL_REASONS } from 'actions/history-actions';
import { ID_OTHER_REASON } from 'constants/app-constants';
import { getDeliveryID, isMultiStopDelivery } from 'helper/order';
import { pushNotificationAction } from 'actions/app-actions';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';

import { buildCredentials } from '../../helper/session-helper';

const NEW_HISTORY_EPIC_CONSTANT = {
  fetchLastCompletedOrderEpic: {
    offset: 0,
    limit: 1,
    interval: 30,
    orderStates: ['COMPLETED'],
  },
};

const sortDeliveryByDate = deliveriesV2 => {
  const deliveries = _get(deliveriesV2, 'deliveries');
  return _orderBy(deliveries, 'createMeta.createAtInTs', 'desc');
};

const buildCompanyGetHistoryURLSearchParams = ({
  offset,
  limit,
  startDate,
  endDate,
  states,
  companyID,
}) => {
  const searchParams = new URLSearchParams({
    'params.offset': offset,
    'params.limit': limit,
    'params.fromDate': startDate,
    'params.toDate': endDate,
    'params.companyID': companyID,
  });
  states.forEach(s => searchParams.append('params.states', s));
  return searchParams;
};

const buildPaxGetHistoryURLSearchParams = ({ startDate, endDate, limit }) => {
  const searchParams = new URLSearchParams({
    'input.startDate': startDate,
    'input.endDate': endDate,
    'input.limit': limit,
  });
  return searchParams;
};

const buildURLSearchParams = ({ isPax, companyID, constant }) => {
  const currentDate = moment();
  const toDate = currentDate.format('YYYY-MM-DD');
  const fromDate = currentDate
    .subtract(constant.interval, 'days')
    .format('YYYY-MM-DD');

  if (isPax) {
    return buildPaxGetHistoryURLSearchParams({
      limit: constant.limit,
      startDate: fromDate,
      endDate: toDate,
    });
  }
  return buildCompanyGetHistoryURLSearchParams({
    offset: constant.offset,
    limit: constant.limit,
    startDate: fromDate,
    endDate: toDate,
    states: constant.orderStates,
    companyID,
  });
};

const fetchLastCompletedOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewHistoryDuck.types.FETCH_LAST_COMPLETED_ORDER),
    mergeMap(action => {
      const constant = NEW_HISTORY_EPIC_CONSTANT.fetchLastCompletedOrderEpic;
      const companyID = userCompanyIDSelector(state$.value);
      const isPax = isPaxSelector(state$.value);

      const urlSearchParams = buildURLSearchParams({
        isPax,
        companyID,
        constant,
      });

      const endpoint = isPax
        ? endpoints.getPaxHistoryDeliveriesEpt(urlSearchParams.toString())
        : endpoints.getHistoryDeliveriesEpt(urlSearchParams.toString());
      return fetchObservableUtil(
        endpoint,
        {
          method: 'GET',
          headers: buildHeadersV2({
            isPax,
            companyID,
            includeInternalHeaders: true,
          }),
          credentials: buildCredentials(isPax),
        },
        { rethrow: true },
      ).pipe(
        mergeMap(({ result }) => {
          if (_get(result, 'totalCount', 0) > 0) {
            const senderContact = senderContactSelector(result.deliveries[0]);
            if (senderContact) {
              return of(
                NewBookingDuck.creators.updatePrefillSenderInfo(senderContact),
              );
            }
            return EMPTY;
          }
          return EMPTY;
        }),
        catchError(err => EMPTY),
      );
    }),
  );

const getDeliveriesV3 = (
  isPax,
  enterprise,
  {
    status,
    statusExclude = false,
    source,
    sourceExclude = false,
    startDate,
    endDate,
  },
) =>
  ajax({
    url: endpoints.getDeliveriesV3Ept({
      startUnixTime: moment(startDate).unix(),
      endUnixTime: moment(endDate).unix(),
      status,
      statusExclude,
      source,
      sourceExclude,
    }),
    method: 'GET',
    headers: buildHeadersV2({
      isPax,
      companyID: _get(enterprise, 'companyID'),
      includeInternalHeaders: true,
    }),
    withCredentials: buildAjaxCredentials(isPax),
    timeout: 10000,
  }).pipe(catchError(error => of({ error })));

const fetchHistoryBookingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewHistoryDuck.types.FETCH_HISTORY_BOOKINGS),
    mergeMap(() => {
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const { startDate, endDate } = historyBookingFilterSelector(state$.value);
      return getDeliveriesV3(isPax, enterprise, {
        status: GE_ORDER_ONGOING_STATES,
        statusExclude: true,
        startDate,
        endDate,
      });
    }),
    mergeMap(resp => {
      const deliveriesV2 = _get(resp, 'response.response.deliveriesV2');
      if (deliveriesV2) {
        deliveriesV2.deliveries = sortDeliveryByDate(deliveriesV2);
        return of(
          NewHistoryDuck.creators.setHistoryBookings({
            deliveriesV2,
          }),
        );
      }

      return of(NewHistoryDuck.creators.fetchHistoryBookingsError());
    }),
  );

const fetchOngoingBookingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewHistoryDuck.types.FETCH_ONGOING_BOOKINGS),
    mergeMap(() => {
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      return getDeliveriesV3(isPax, enterprise, {
        status: GE_ORDER_ONGOING_STATES,
        statusExclude: false,
        startDate: moment()
          .subtract(30, 'days')
          .startOf('day')
          .toString(),
        endDate: moment()
          .endOf('day')
          .toString(),
      });
    }),
    mergeMap(resp => {
      const deliveriesV2 = _get(resp, 'response.response.deliveriesV2');
      if (deliveriesV2) {
        deliveriesV2.deliveries = sortDeliveryByDate(deliveriesV2);
        return of(NewHistoryDuck.creators.setOngoingBookings({ deliveriesV2 }));
      }

      return of(NewHistoryDuck.creators.fetchOngoingBookingsError());
    }),
  );

const fetchBookAgainBookingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewHistoryDuck.types.FETCH_BOOK_AGAIN),
    mergeMap(() => {
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const startDate = moment().add(
        isPax
          ? -PAX_ORDER_SUMMARIZE_FETCH_DAYS
          : -GFB_ORDER_SUMMARIZE_FETCH_DAYS,
        'days',
      );
      const endDate = moment();
      return getDeliveriesV3(isPax, enterprise, {
        status: GE_ORDER_BOOK_AGAIN_STATES,
        startDate,
        endDate,
      });
    }),
    mergeMap(resp => {
      const deliveriesV2 = _get(resp, 'response.response.deliveriesV2');
      if (deliveriesV2) {
        deliveriesV2.deliveries = sortDeliveryByDate(deliveriesV2);
        return of(
          NewHistoryDuck.creators.setBookAgain({
            deliveriesV2,
          }),
        );
      }

      return of(NewHistoryDuck.creators.fetchBookAgainError());
    }),
  );

const cancelDeliveriesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewHistoryDuck.types.CANCEL_DELIVERIES),
    mergeMap(action => {
      const { deliveries } = action;
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const enableCancellationRules = enableCancellationRulesSelector(
        state$.value,
      );
      const headers = buildHeadersV2({
        isPax,
        companyID: _get(enterprise, 'companyID'),
        includeInternalHeaders: true,
      });

      const obs = [];
      deliveries.forEach(delivery => {
        const deliveryID = getDeliveryID(delivery);
        const isMSDBooking = isMultiStopDelivery(delivery);
        if (isPax) {
          const expressServiceID = _get(delivery, [
            'service',
            'expressServiceID',
          ]);
          obs.push(
            fetchObservableUtil(
              getPaxCancelDeliveryEpt({
                deliveryID,
                expressServiceID,
              }),
              {
                method: 'DELETE',
                headers,
                credentials: buildCredentials(isPax),
              },
              { rethrow: true },
            ).pipe(catchAndReturnError),
          );
        } else {
          obs.push(
            fetchObservableUtil(
              getCancelDeliveryEpt(deliveryID),
              {
                method: 'DELETE',
                headers,
                credentials: buildCredentials(isPax),
                body: JSON.stringify({
                  deliveryID,
                  isMSDBooking,
                  enableCancellationRules,
                }),
              },
              { rethrow: true },
            ).pipe(catchAndReturnError),
          );
        }
      });

      return forkJoin(obs);
    }),
    mergeMap(results => {
      if (!results) {
        return EMPTY;
      }

      const successCount = results.filter(result => !result.error).length;
      const failedCount = results.length - successCount;
      return of(
        pushNotificationAction({
          id: 'cancelDeliveries',
          type:
            successCount > 0
              ? NOTIFICATION_TYPE.CONFIRMED
              : NOTIFICATION_TYPE.WARNING,
          message:
            successCount > 0
              ? `Cancelled ${successCount} deliveries successfully`
              : 'Failed to cancel all deliveries',
          description:
            (successCount > 0 &&
              failedCount > 0 &&
              `Failed to cancel ${failedCount} deliveries`) ||
            '',
        }),
        NewHistoryDuck.creators.fetchOngoingBookings(),
        NewHistoryDuck.creators.fetchHistoryBookings(),
        NewHistoryDuck.creators.updateOngoingBookingSelection([]),
      );
    }),
  );

const submitCancelReasonEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewHistoryDuck.types.SUBMIT_CANCEL_REASON_FOR_DELIVERIES),
    mergeMap(action => {
      const { deliveries, cancelRequest } = action;

      if (!_get(cancelRequest, 'feedback.comment')) {
        return EMPTY;
      }

      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const headers = buildHeadersV2({
        isPax,
        companyID: _get(enterprise, 'companyID'),
        includeInternalHeaders: true,
      });

      const obs = [];
      deliveries.forEach(delivery => {
        const isMSDBooking = isMultiStopDelivery(delivery);
        const expressServiceID = _get(delivery, 'service.expressServiceID');
        const req = {
          ...cancelRequest,
          deliveryID: getDeliveryID(delivery),
          isMSDBooking,
          expressServiceID,
        };
        obs.push(
          fetchObservableUtil(getCancelReasonsEpt(), {
            method: 'POST',
            headers,
            credentials: buildCredentials(isPax),
            body: JSON.stringify({ req }),
          }),
        );
      });

      return forkJoin(obs);
    }),
    mergeMap(() => EMPTY),
  );

const listCancellationReasonsEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewHistoryDuck.types.LIST_CANCELLATION_REASONS),
    mergeMap(action => {
      const { deliveries } = action;
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const headers = buildHeadersV2({
        isPax,
        companyID: _get(enterprise, 'companyID'),
        includeInternalHeaders: true,
      });

      const obs = [];
      deliveries.forEach(delivery => {
        const expressServiceID = _get(delivery, 'service.expressServiceID', 0);
        const deliveryID = getDeliveryID(delivery);
        obs.push(
          fetchObservableUtil(
            getCancelReasonsWithDeliveryID(deliveryID, expressServiceID),
            {
              method: 'GET',
              headers,
              credentials: buildCredentials(isPax),
            },
          ),
        );
      });

      return forkJoin(obs);
    }),
    mergeMap(responses => {
      const allCancellationReasons = _compact(
        responses.map(resp => resp.cancelReasons),
      );
      if (!allCancellationReasons || allCancellationReasons.length === 0) {
        return EMPTY;
      }
      let cancelReasons = allCancellationReasons[0];
      if (allCancellationReasons.length > 1) {
        cancelReasons = allCancellationReasons[0].filter(cancelReason =>
          _every(allCancellationReasons, rs =>
            rs.find(r => r.id === cancelReason.id),
          ),
        );
      }
      cancelReasons = [
        ...cancelReasons.filter(({ id }) => id !== ID_OTHER_REASON),
        ...cancelReasons.filter(({ id }) => id === ID_OTHER_REASON),
      ];
      return of({
        type: FETCH_CANCEL_REASONS.SUCCESS,
        data: {
          cancelReasons,
        },
      });
    }),
  );

export default combineEpics(
  fetchLastCompletedOrderEpic,
  fetchHistoryBookingsEpic,
  fetchOngoingBookingsEpic,
  fetchBookAgainBookingsEpic,
  cancelDeliveriesEpic,
  submitCancelReasonEpic,
  listCancellationReasonsEpic,
);
